import { oxAuthHeaders } from './guestId.js';

export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export type UploadedAsset = {
  assetId: string;
  widthPx: number;
  heightPx: number;
  thumbUrl?: string;
  proxyUrl?: string;
  originalUrl?: string;
};

/** Presign → PUT → complete. Returns asset metadata. */
export async function uploadImageFile(
  apiBase: string,
  file: Blob,
  opts: {
    filename?: string;
    contentType?: string;
    token?: string | null;
    widthPx?: number;
    heightPx?: number;
  } = {},
): Promise<UploadedAsset> {
  const base = apiBase.replace(/\/$/, '');
  const buf = await file.arrayBuffer();
  const hash = await sha256Hex(buf);
  let widthPx = opts.widthPx ?? 0;
  let heightPx = opts.heightPx ?? 0;
  if (!widthPx || !heightPx) {
    const bmp = await createImageBitmap(file);
    widthPx = bmp.width;
    heightPx = bmp.height;
    bmp.close();
  }

  const authHeaders = oxAuthHeaders(opts.token);

  const contentType =
    opts.contentType || (file instanceof File ? file.type : '') || 'image/png';
  const filename = opts.filename || (file instanceof File ? file.name : 'image.png');

  const presignRes = await fetch(`${base}/assets/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      filename,
      contentType,
      sha256: hash,
      widthPx,
      heightPx,
      byteLength: buf.byteLength,
    }),
  });
  const presign = await presignRes.json();
  if (!presignRes.ok || !presign.assetId) {
    throw new Error(presign.error || 'presign_failed');
  }

  if (presign.uploadUrl) {
    const putRes = await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: (presign.headers as Record<string, string> | undefined) ?? {
        'Content-Type': contentType,
      },
      body: file,
    });
    if (!putRes.ok) throw new Error(`upload_failed_${putRes.status}`);
  }

  const completeRes = await fetch(`${base}/assets/${presign.assetId}/complete`, {
    method: 'POST',
    headers: authHeaders,
  });
  const complete = await completeRes.json().catch(() => ({}));
  if (!completeRes.ok) {
    throw new Error((complete as { error?: string }).error || `complete_failed_${completeRes.status}`);
  }

  let proxyUrl = (complete as { proxyUrl?: string }).proxyUrl;
  let thumbUrl = (complete as { thumbUrl?: string }).thumbUrl;
  const status = (complete as { status?: string }).status;
  // Brief wait for derivatives — editor keeps a local blob preview and unlocks
  // Usar imagen via assetId without needing this to finish.
  if (status === 'pending') {
    for (let i = 0; i < 8; i++) {
      await new Promise((r) => setTimeout(r, 350));
      const meta = await fetchAssetMeta(base, presign.assetId as string, opts.token);
      if (meta?.proxyUrl || meta?.thumbUrl) {
        proxyUrl = meta.proxyUrl ?? proxyUrl;
        thumbUrl = meta.thumbUrl ?? thumbUrl;
        if (proxyUrl) {
          try {
            const head = await fetch(proxyUrl, { method: 'GET' });
            if (head.ok) break;
          } catch {
            /* keep polling */
          }
        }
      }
    }
  }

  return {
    assetId: presign.assetId as string,
    widthPx,
    heightPx,
    thumbUrl,
    proxyUrl,
  };
}

export async function fetchAssetMeta(
  apiBase: string,
  assetId: string,
  token?: string | null,
): Promise<{
  thumbUrl?: string;
  proxyUrl?: string;
  originalUrl?: string;
  widthPx?: number;
  heightPx?: number;
  status?: string;
} | null> {
  if (!assetId?.trim()) return null;
  const base = apiBase.replace(/\/$/, '');
  const res = await fetch(`${base}/assets/${encodeURIComponent(assetId)}`, {
    headers: oxAuthHeaders(token),
  });
  if (!res.ok) return null;
  return res.json();
}
